import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getWeekRange } from '../../utils/movieUtils';
import { localDateKey, todayKey } from '../../utils/dateKeys';

const ArchiveCalendar = ({ 
  archivedWeeks, 
  selectedYear,
  onYearChange,
  getAvailableYears,
  timeSlots,
  getScheduledMovieForSlot,
  isSlotOccupied,
  timezone,
  viewerTimezone,
  convertTimeToViewerTimezone,
  onMovieClick,
  isAdmin = false,
  onUnarchiveWeek
}) => {
  // Mobile navigation state for archived calendars
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);

  // Mobile navigation helpers for archived calendars
  const getCurrentArchivedWeek = () => archivedWeeks[selectedWeekIndex];
  const getCurrentArchivedDay = () => {
    const currentWeek = getCurrentArchivedWeek();
    return currentWeek ? currentWeek.week[selectedDayIndex] : null;
  };
  
  const navigateArchivedDay = (direction) => {
    const currentWeek = getCurrentArchivedWeek();
    if (!currentWeek) return;
    
    if (direction === 'next') {
      if (selectedDayIndex < currentWeek.week.length - 1) {
        setSelectedDayIndex(selectedDayIndex + 1);
      } else if (selectedWeekIndex < archivedWeeks.length - 1) {
        setSelectedWeekIndex(selectedWeekIndex + 1);
        setSelectedDayIndex(0);
      }
    } else if (direction === 'prev') {
      if (selectedDayIndex > 0) {
        setSelectedDayIndex(selectedDayIndex - 1);
      } else if (selectedWeekIndex > 0) {
        setSelectedWeekIndex(selectedWeekIndex - 1);
        setSelectedDayIndex(archivedWeeks[selectedWeekIndex - 1].week.length - 1);
      }
    }
  };

  const canNavigateArchivedPrev = () => {
    return selectedWeekIndex > 0 || selectedDayIndex > 0;
  };

  const canNavigateArchivedNext = () => {
    const currentWeek = getCurrentArchivedWeek();
    return selectedWeekIndex < archivedWeeks.length - 1 || 
           (currentWeek && selectedDayIndex < currentWeek.week.length - 1);
  };

  // Set initial navigation to current date when archived weeks are available
  React.useEffect(() => {
    if (archivedWeeks && archivedWeeks.length > 0) {
      // The LOCAL date, not toISOString()'s UTC one - see utils/dateKeys.
      const todayDateString = todayKey();

      // Find the archived week and day that contains today's date
      let foundWeekIndex = -1;
      let foundDayIndex = -1;

      for (let weekIndex = 0; weekIndex < archivedWeeks.length; weekIndex++) {
        const archivedWeek = archivedWeeks[weekIndex];
        if (archivedWeek && archivedWeek.week) {
          for (let dayIndex = 0; dayIndex < archivedWeek.week.length; dayIndex++) {
            const day = archivedWeek.week[dayIndex];
            const dayDateString = localDateKey(day);
            
            if (dayDateString === todayDateString) {
              foundWeekIndex = weekIndex;
              foundDayIndex = dayIndex;
              break;
            }
          }
        }
        if (foundWeekIndex !== -1) break;
      }
      
      // If we found today's date in archived weeks, set the navigation to show it
      if (foundWeekIndex !== -1 && foundDayIndex !== -1) {
        console.log(`📦 Found today's date in archived calendar - Week ${foundWeekIndex + 1}, Day ${foundDayIndex + 1}`);
        setSelectedWeekIndex(foundWeekIndex);
        setSelectedDayIndex(foundDayIndex);
      } else {
        console.log('📦 Today\'s date not found in archived weeks, using default (first week/day)');
        // Default to first week/day if today is not in the archived calendar
        setSelectedWeekIndex(0);
        setSelectedDayIndex(0);
      }
    }
  }, [archivedWeeks]); // Run when archivedWeeks changes

  if (getAvailableYears().length === 0) {
    return (
      <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl p-12 text-center">
        <div className="mb-4 flex justify-center">
          <img 
            src="/Sarcophagus.webp" 
            alt="Sarcophagus" 
            className="w-16 h-16 object-contain opacity-75"
          />
        </div>
        <h3 className="text-lg font-bold text-orange-300 mb-2">No Archived Calendars</h3>
        <p className="text-orange-200">
          No weeks have been archived yet. Archived weeks will appear here for historical viewing.
        </p>
      </div>
    );
  }

  if (archivedWeeks.length === 0) {
    return (
      <div>
        {/* Year Selection */}
        <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-orange-300 mb-1">📦 Archive Year Selection</h3>
              <p className="text-orange-200 text-sm">Select a year to view archived movie schedules</p>
            </div>
            <select
              value={selectedYear}
              onChange={(e) => onYearChange(e.target.value)}
              className="bg-gray-700 border-2 border-orange-600 text-orange-100 rounded-md px-4 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-400"
            >
              {getAvailableYears().map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl p-12 text-center">
          <div className="mb-4 flex justify-center">
            <img 
              src="/Sarcophagus.webp" 
              alt="Sarcophagus" 
              className="w-16 h-16 object-contain opacity-75"
            />
          </div>
          <h3 className="text-lg font-bold text-orange-300 mb-2">No Archives for {selectedYear}</h3>
          <p className="text-orange-200">No weeks from {selectedYear} have been archived yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Year Selection */}
      <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-orange-300 mb-1">📦 Archive Year Selection</h3>
            <p className="text-orange-200 text-sm">Select a year to view archived movie schedules</p>
          </div>
          <select
            value={selectedYear}
            onChange={(e) => onYearChange(e.target.value)}
            className="bg-gray-700 border-2 border-orange-600 text-orange-100 rounded-md px-4 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-400"
          >
            {getAvailableYears().map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Archived Calendar Display */}
      <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-purple-900 to-orange-900 px-4 py-3 border-b-2 border-orange-500">
          <h2 className="text-xl font-bold text-orange-300 flex items-center gap-2">
            <img 
              src="/Sarcophagus.webp" 
              alt="Sarcophagus" 
              className="w-5 h-5 object-contain"
            />
            Archived Movies from {selectedYear} ({viewerTimezone})
          </h2>
          <p className="text-orange-200 text-sm">
            {archivedWeeks.length} archived weeks • All movies locked as "Already watched"
            {viewerTimezone !== timezone && (
              <span> • Times converted from {timezone}</span>
            )}
          </p>
        </div>
        
        {/* Desktop View - Hidden on mobile */}
        <div className="hidden lg:block max-h-[70vh] overflow-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#f97316 #374151' }}>
          {archivedWeeks.map((archivedWeekData, weekIndex) => (
            <div key={archivedWeekData.id || weekIndex} className="mb-6">
              <div className="bg-purple-700 px-4 py-2 border-b border-orange-500 flex justify-between items-center">
                <h3 className="text-orange-300 font-bold text-sm flex items-center gap-2">
                  📦 Archived Week: {getWeekRange(archivedWeekData.week)}
                  <span className="text-xs text-purple-200 ml-2">
                    (Archived: {archivedWeekData.archivedDate.toLocaleDateString()})
                  </span>
                </h3>
                
                {/* Unarchive Button - Admin Only */}
                {isAdmin && onUnarchiveWeek && (
                  <button
                    onClick={() => onUnarchiveWeek(archivedWeekData)}
                    className="bg-green-600 hover:bg-green-500 text-white px-3 py-1 rounded-md text-xs font-bold transition-colors"
                    title="Restore this week to active schedule"
                  >
                    📤 Unarchive
                  </button>
                )}
              </div>
              
              {/* Simplified archive week view - just the grid without complex interactions */}
              <div className="grid grid-cols-8 gap-0">
                <div className="bg-gray-700 border-r border-orange-500 p-2">
                  <div className="text-orange-300 font-bold text-xs text-center">Time</div>
                </div>
                
                {archivedWeekData.week.map((date, dayIndex) => (
                  <div key={dayIndex} className="bg-gray-700 border-r border-orange-500 p-2 text-center">
                    <div className="text-orange-300 font-bold text-xs">
                      {date.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className="text-orange-200 text-xs">
                      {date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}
                    </div>
                  </div>
                ))}
                
                {timeSlots.filter(timeSlot => timeSlot && timeSlot.id).map((timeSlot, slotIndex) => (
                  <React.Fragment key={timeSlot.id}>
                    <div className="bg-gray-900 border-r border-b border-orange-500 p-2 text-center">
                      <div className="text-orange-400 text-xs font-bold">
                        {convertTimeToViewerTimezone ? 
                          convertTimeToViewerTimezone(timeSlot.time, timezone) :
                          timeSlot.time
                        }
                      </div>
                    </div>
                    
                    {archivedWeekData.week.map((date, dayIndex) => {
                      const scheduledMovie = getScheduledMovieForSlot(date, timeSlot, archivedWeekData);
                      const isOccupied = isSlotOccupied(date, timeSlot, archivedWeekData);
                      const isFirstSlotOfMovie = scheduledMovie && scheduledMovie.timeSlot && scheduledMovie.timeSlot.id === timeSlot.id;
                      
                      return (
                        <div
                          key={`${dayIndex}-${slotIndex}`}
                          className={`border-r border-b border-orange-500 h-16 relative ${
                            isOccupied ? 'bg-purple-800' : 'bg-gray-800'
                          }`}
                        >
                          {isFirstSlotOfMovie && scheduledMovie && (
                            <div 
                              className={`absolute inset-0 ${scheduledMovie.movie.color} border-2 border-orange-400 rounded m-1 flex flex-col justify-center items-center text-white text-xs font-bold overflow-hidden cursor-pointer hover:border-yellow-400 transition-colors`}
                              style={{ 
                                height: `${scheduledMovie.slotsSpanned * 4 - 0.5}rem`,
                                zIndex: 10 
                              }}
                              title={`${scheduledMovie.movie.title} - Archived • Click for details`}
                              onClick={() => onMovieClick && onMovieClick(scheduledMovie)}
                            >
                              <div className="text-center px-1 leading-tight flex-1 flex flex-col justify-center">
                                <div className="truncate text-xs font-bold">
                                  {scheduledMovie.movie.title.length > 15 
                                    ? scheduledMovie.movie.title.substring(0, 15) + '...'
                                    : scheduledMovie.movie.title}
                                </div>
                                <div className="text-xs opacity-90 mb-1">
                                  {scheduledMovie.movie.runtime}min
                                </div>
                                
                                {/* Archived Badge */}
                                <div className="bg-purple-600 text-white px-1 py-0.5 rounded text-xs opacity-90">
                                  📦 Archived
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Mobile View - Single day with navigation */}
        <div className="lg:hidden">
          {archivedWeeks.length > 0 && getCurrentArchivedDay() && (
            <div className="space-y-4">
              {/* Mobile Archive Navigation */}
              <div className="bg-purple-700 border-2 border-orange-500 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={() => navigateArchivedDay('prev')}
                    disabled={!canNavigateArchivedPrev()}
                    className={`p-2 rounded ${
                      canNavigateArchivedPrev() 
                        ? 'text-orange-300 hover:bg-orange-600/20' 
                        : 'text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <ChevronLeft size={20} />
                  </button>
                  
                  <div className="text-center">
                    <h3 className="text-lg font-bold text-orange-300 flex items-center justify-center gap-2">
                      <img 
                        src="/Sarcophagus.webp" 
                        alt="Sarcophagus" 
                        className="w-4 h-4 object-contain"
                      />
                      {getCurrentArchivedDay().toLocaleDateString('en-US', { 
                        weekday: 'long',
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </h3>
                    <div className="text-sm text-orange-200">
                      Archived from {selectedYear} • Day {selectedDayIndex + 1} of {getCurrentArchivedWeek()?.week.length || 0}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => navigateArchivedDay('next')}
                    disabled={!canNavigateArchivedNext()}
                    className={`p-2 rounded ${
                      canNavigateArchivedNext() 
                        ? 'text-orange-300 hover:bg-orange-600/20' 
                        : 'text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>

                {/* Week Info and Unarchive Button */}
                <div className="flex items-center justify-between text-sm">
                  <div className="text-purple-200">
                    Week: {getWeekRange(getCurrentArchivedWeek()?.week)} 
                    <span className="ml-2">
                      (Archived: {getCurrentArchivedWeek()?.archivedDate.toLocaleDateString()})
                    </span>
                  </div>
                  
                  {/* Mobile Unarchive Button - Admin Only */}
                  {isAdmin && onUnarchiveWeek && (
                    <button
                      onClick={() => onUnarchiveWeek(getCurrentArchivedWeek())}
                      className="bg-green-600 hover:bg-green-500 text-white px-3 py-1 rounded-md text-xs font-bold transition-colors"
                      title="Restore this week to active schedule"
                    >
                      📤 Unarchive
                    </button>
                  )}
                </div>
              </div>

              {/* Mobile Day Schedule */}
              <div className="bg-gray-800 border-2 border-orange-600 rounded-lg overflow-hidden">
                <div className="divide-y divide-gray-600">
                  {timeSlots.filter(timeSlot => timeSlot && timeSlot.id).map(timeSlot => {
                    const currentDay = getCurrentArchivedDay();
                    const currentWeek = getCurrentArchivedWeek();
                    const scheduledMovie = getScheduledMovieForSlot(currentDay, timeSlot, currentWeek);
                    const isOccupied = isSlotOccupied(currentDay, timeSlot, currentWeek);
                    const isFirstSlotOfMovie = scheduledMovie && scheduledMovie.timeSlot && scheduledMovie.timeSlot.id === timeSlot.id;
                    
                    return (
                      <div 
                        key={timeSlot.id} 
                        className={`flex min-h-[80px] ${
                          isOccupied ? 'bg-purple-800/30' : 'bg-gray-800'
                        }`}
                      >
                        {/* Time Column */}
                        <div className="w-20 flex-shrink-0 p-3 bg-gray-700 border-r border-orange-600 flex items-center justify-center">
                          <div className="text-sm font-semibold text-orange-200 text-center">
                            {convertTimeToViewerTimezone ? 
                              convertTimeToViewerTimezone(timeSlot.time, timezone) :
                              timeSlot.time
                            }
                          </div>
                        </div>
                        
                        {/* Content Column */}
                        <div className="flex-1 p-3 relative">
                          {isFirstSlotOfMovie && scheduledMovie ? (
                            <div 
                              className={`h-full rounded cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg ${scheduledMovie.movie.color || 'bg-orange-600'} text-white p-3 flex flex-col justify-center border-2 border-orange-400`}
                              onClick={() => onMovieClick && onMovieClick(scheduledMovie)}
                            >
                              <div className="font-bold text-base mb-1">{scheduledMovie.movie.title}</div>
                              <div className="text-sm opacity-75 mb-2">{scheduledMovie.movie.runtime}min • {scheduledMovie.movie.year}</div>
                              
                              {/* Archived Badge */}
                              <div className="bg-purple-600 text-white px-2 py-1 rounded text-xs font-bold self-start">
                                📦 Archived
                              </div>
                            </div>
                          ) : isOccupied ? (
                            <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                              {/* Movie continues from previous slot */}
                            </div>
                          ) : (
                            <div className="h-full flex items-center justify-center text-gray-500 text-sm">
                              No movie scheduled
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArchiveCalendar;

